import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getEnterKeyShortcutPreference, setEnterKeyShortcutPreference } from '../../utils/teacherSettings';

interface TeacherSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSettingChange?: () => void; // Callback when setting changes
}

const ModalBackdrop = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 3000;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
  }
`;

const SettingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const SettingInfo = styled.div`
  flex: 1;
  padding-right: 16px;
`;

const SettingLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
`;

const SettingDescription = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
`;

// Toggle Switch Component
const ToggleSwitch = styled.button<{ $enabled: boolean }>`
  width: 52px;
  height: 28px;
  border-radius: 14px;
  border: none;
  background: ${props => props.$enabled ? '#10b981' : '#cbd5e1'};
  color: white;
  cursor: pointer;
  position: relative;
  transition: background 0.3s ease;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: white;
    top: 3px;
    left: ${props => props.$enabled ? '27px' : '3px'};
    transition: left 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:hover {
    background: ${props => props.$enabled ? '#059669' : '#94a3b8'};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TeacherSettingsModal: React.FC<TeacherSettingsModalProps> = ({
  isOpen,
  onClose,
  onSettingChange
}) => {
  const [enterKeyEnabled, setEnterKeyEnabled] = useState(false);

  // Load setting on mount
  useEffect(() => {
    if (isOpen) {
      setEnterKeyEnabled(getEnterKeyShortcutPreference());
    }
  }, [isOpen]);

  const handleToggleEnterKey = () => {
    const newValue = !enterKeyEnabled;
    setEnterKeyEnabled(newValue);
    setEnterKeyShortcutPreference(newValue);
    onSettingChange?.(); // Notify parent to re-check setting
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <ModalBackdrop
      $isOpen={isOpen}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <ModalContent>
        <ModalHeader>
          <ModalTitle>⚙️ Tənzimləmələr</ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </ModalHeader>

        <SettingItem>
          <SettingInfo>
            <SettingLabel>Enter tuşu ilə təsdiqlə</SettingLabel>
            <SettingDescription>
              Aktiv edildikdə, Enter tuşuna basaraq qiymətləndirməni təsdiqləyə bilərsiniz
            </SettingDescription>
          </SettingInfo>
          <ToggleSwitch
            $enabled={enterKeyEnabled}
            onClick={handleToggleEnterKey}
            aria-label={enterKeyEnabled ? 'Enter tuşu aktivdir' : 'Enter tuşu deaktivdir'}
          />
        </SettingItem>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default TeacherSettingsModal;
